'use client';

import React, { useState, useEffect } from 'react';

import { useTranslations } from 'next-intl';
import { Progress } from '@/components/ui/progress';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '@/components/ui/card';
import {
  Loader2,
  Radio,
  Database,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  AlertCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from '@/i18n/navigation';
import { logger } from '@/lib/logger';

/**
 * 视频生成进度组件
 * 支持两种模式获取任务状态：
 * 1. 直接从API轮询（默认）
 * 2. 从数据库轮询 (推荐，与webhook配合使用)
 *
 * @param {Object} props
 * @param {string} props.status - 当前状态
 * @param {number} props.progress - 当前进度
 * @param {string} props.statusText - 状态说明文字
 * @param {boolean} props.isWebhook - 是否使用webhook回调模式
 * @param {string} props.taskId - 任务ID
 * @param {Function} props.onStatusUpdate - 状态更新回调
 * @param {boolean} props.hasAccess - 用户是否有付费权限
 * @param {Function} props.onUpgrade - 升级按钮点击回调
 * @param {string} props.apiType - 用于轮询的API类型，默认为'pix'，可以是'veo3'
 * @param {number} props.estimatedTime - 预估完成时间（秒），如果不传则根据用户类型自动计算
 */
const GenerationProgress = ({
  status,
  progress = 0,
  statusText,
  isWebhook = false,
  taskId,
  onStatusUpdate,
  hasAccess = true,
  onUpgrade,
  apiType = 'pix',
  estimatedTime,
}) => {
  // 🔥 LCP优化：使用React.memo包装，避免不必要的重渲染
  const t = useTranslations('pix');
  const [currentProgress, setCurrentProgress] = useState(progress || 5);
  const [currentStatus, setCurrentStatus] = useState(status || 'pending');
  const [currentStatusText, setCurrentStatusText] = useState(statusText || '');
  const [videoUrl, setVideoUrl] = useState('');
  const [error, setError] = useState(null);
  // 修改为固定队列位置（免费用户固定为4）
  const [queuePosition] = useState(hasAccess ? Math.floor(Math.random() * 2) + 1 : 4);
  
  // 添加计时器状态
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  // 预估时间：优先使用传入的参数，否则根据用户类型设置默认值
  // 免费用户5分钟，付费用户3分钟
  const actualEstimatedTime = estimatedTime || (hasAccess ? 180 : 300);

  // 🔥 LCP优化：延迟进度模拟，避免阻塞初始渲染
  useEffect(() => {
    // 只有在处理中状态才进行模拟进度
    if (currentStatus !== 'processing' && currentStatus !== 'pending') {
      return;
    }

    // 设置初始进度
    if (currentStatus === 'pending' && currentProgress < 10) {
      setCurrentProgress(10);
    }

    // 🔥 关键优化：延迟启动进度模拟，让LCP元素先渲染
    const startProgressSimulation = () => {
      // 移动端检测
      const isMobile = window.innerWidth <= 768;
      
      // 进度增长逻辑 - 移动端使用更激进的优化
      let progressInterval;
      const simulateProgress = () => {
        setCurrentProgress((prevProgress) => {
          // 付费用户进度增长更快
          const increment = hasAccess ? 2.5 : 1;

          // 根据当前进度动态调整增量，越接近90越慢
          const adjustedIncrement =
            prevProgress < 30
              ? increment * 1.5
              : prevProgress < 60
                ? increment
                : prevProgress < 80
                  ? increment * 0.5
                  : increment * 0.2;

          // 确保进度不超过某个上限（处理中状态下不应到达100%）
          const maxProgress = currentStatus === 'pending' ? 30 : 90;
          return Math.min(prevProgress + adjustedIncrement, maxProgress);
        });
      };

      // 移动端使用更长的更新间隔
      const mobileInterval = hasAccess ? 4000 : 6000; // 移动端4-6秒
      const desktopInterval = hasAccess ? 2000 : 4000; // 桌面端2-4秒
      const updateInterval = isMobile ? mobileInterval : desktopInterval;

      // 使用requestIdleCallback优化主线程性能
      const scheduleProgressUpdate = () => {
        if (typeof requestIdleCallback !== 'undefined') {
          requestIdleCallback(() => {
            simulateProgress();
            // 移动端使用更长的更新间隔
            progressInterval = setTimeout(scheduleProgressUpdate, updateInterval);
          }, { timeout: isMobile ? 2000 : 1000 }); // 移动端更长的超时时间
        } else {
          // 降级到setTimeout，但使用更长的间隔
          progressInterval = setTimeout(simulateProgress, updateInterval);
        }
      };

      // 启动进度更新
      scheduleProgressUpdate();

      return () => {
        if (progressInterval) {
          clearTimeout(progressInterval);
        }
      };
    };

    // 🔥 关键优化：延迟启动，让LCP元素先渲染完成
    const delayStart = setTimeout(startProgressSimulation, 100);
    
    return () => {
      clearTimeout(delayStart);
    };
  }, [currentStatus, hasAccess, currentProgress]);

  // 初始化开始时间
  useEffect(() => {
    if ((currentStatus === 'processing' || currentStatus === 'pending') && !startTime) {
      setStartTime(Date.now());
    }
  }, [currentStatus, startTime]);

  // 更新已等待时间 - 移动端优化更新频率
  useEffect(() => {
    let timer;
    if (startTime && (currentStatus === 'processing' || currentStatus === 'pending')) {
      // 移动端使用更长的更新间隔
      const isMobile = window.innerWidth <= 768;
      const updateInterval = isMobile ? 3000 : 2000; // 移动端3秒，桌面端2秒
      
      timer = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setElapsedTime(elapsed);
      }, updateInterval);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [startTime, currentStatus]);

  // 格式化时间显示
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}${t('GenerationProgress.time.minutes')} ${secs}${t('GenerationProgress.time.seconds')}`;
  };

  // 状态映射到UI显示内容
  const statusMap = {
    pending: {
      title: t('GenerationProgress.status.initializing'),
      description: t('GenerationProgress.descriptions.initializing', {
        estimatedTime: formatTime(actualEstimatedTime),
        elapsedTime: formatTime(elapsedTime)
      }),
      progress: currentProgress || 10,
      showSpinner: true,
      icon: Loader2,
      iconClass: 'text-green-400 animate-spin',
      bgClass: 'bg-green-500/10',
    },
    processing: {
      title: t('GenerationProgress.status.processing'),
      description: t('GenerationProgress.descriptions.processing', {
        estimatedTime: formatTime(actualEstimatedTime),
        elapsedTime: formatTime(elapsedTime)
      }),
      progress: currentProgress || 35,
      showSpinner: true,
      icon: Loader2,
      iconClass: 'text-green-400 animate-spin',
      bgClass: 'bg-green-500/10',
    },
    completed: {
      title: t('GenerationProgress.status.completed'),
      description: t('GenerationProgress.descriptions.completed'),
      progress: 100,
      showSpinner: false,
      icon: CheckCircle2,
      iconClass: 'text-green-400',
      bgClass: 'bg-green-500/10',
    },
    failed: {
      title: t('GenerationProgress.status.failed'),
      description: t('GenerationProgress.descriptions.failed'),
      progress: 100,
      showSpinner: false,
      isError: true,
      icon: XCircle,
      iconClass: 'text-red-400',
      bgClass: 'bg-red-500/10',
    },
    unknown: {
      title: t('GenerationProgress.status.unknown'),
      description: t('GenerationProgress.descriptions.unknown'),
      progress: 0,
      showSpinner: false,
      isError: true,
      icon: AlertTriangle,
      iconClass: 'text-amber-400',
      bgClass: 'bg-amber-500/10',
    },
  };

  // 轮询视频状态 - 移动端TBT优化
  useEffect(() => {
    if (!taskId || currentStatus === 'completed' || currentStatus === 'failed') {
      return;
    }

    let intervalId;
    const pollStatus = async () => {
      try {
        // 根据apiType和webhook模式决定使用哪个API端点
        let apiEndpoint;
        if (apiType === 'veo3') {
          // VEO3专用API端点
          apiEndpoint = isWebhook
            ? `/api/veo3/video-db-status?taskId=${taskId}`
            : `/api/veo3/detail/${taskId}`;
        } else {
          // 默认Pixverse API端点
          apiEndpoint = isWebhook
            ? `/api/pix/video-db-status?taskId=${taskId}`
            : `/api/pix/video-status?taskId=${taskId}`;
        }

        logger.info(`Polling ${apiType.toUpperCase()} video status ${isWebhook ? 'from database' : 'from API'}:`, taskId);

        const response = await fetch(apiEndpoint);
        if (!response.ok) {
          throw new Error(`Status poll failed: ${response.status}`);
        }

        // 添加响应文本检查，防止解析undefined或无效JSON
        const responseText = await response.text();
        if (!responseText || responseText.trim() === '' || responseText === 'undefined') {
          throw new Error('Empty or invalid response from server');
        }

        let statusData;
        try {
          statusData = JSON.parse(responseText);
        } catch (jsonError) {
          logger.error(`Failed to parse JSON response: ${responseText}`, jsonError);
          throw new Error(`Invalid JSON response: ${responseText.substring(0, 100)}...`);
        }

        logger.info(`Received ${apiType.toUpperCase()} video status update:`, statusData);

        // 更新状态
        setCurrentStatus(statusData.status);
        // 对于完成状态，立即设置进度为100%
        if (statusData.status === 'completed' || statusData.status === 'failed') {
          setCurrentProgress(100);
        }

        if (statusData.message) {
          setCurrentStatusText(statusData.message);
        }

        // 如果状态是completed，设置视频URL
        if (statusData.status === 'completed') {
          // 优先使用带水印的视频URL，如果没有则使用普通视频URL
          const videoUrlToUse = statusData.waterMarkedUrl || statusData.video_url;
          setVideoUrl(videoUrlToUse);
        }

        // 如果提供了回调函数，通知父组件状态更新
        if (onStatusUpdate) {
          // 传递状态、默认URL和完整的视频数据对象
          onStatusUpdate(
            statusData.status,
            statusData.video_url,
            statusData.data, // 传递完整数据对象
          );
        }

        // 如果状态是终态，清除轮询
        if (statusData.status === 'completed' || statusData.status === 'failed') {
          clearInterval(intervalId);
        }
      } catch (error) {
        logger.error('Failed to poll video status:', error);
        setError(error.message);

        // 连续几次失败后，可以考虑停止轮询
        // 此处仅记录错误，继续尝试轮询
      }
    };

    // 移动端检测和智能轮询策略
    const isMobile = window.innerWidth <= 768;
    const mobilePollInterval = 20000; // 移动端20秒
    const desktopPollInterval = 15000; // 桌面端15秒
    const pollInterval = isMobile ? mobilePollInterval : desktopPollInterval;

    // 🔥 LCP优化：延迟轮询启动，避免阻塞初始渲染
    const startPolling = () => {
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(() => {
          pollStatus();
          // 移动端使用更长的轮询间隔
          intervalId = setInterval(pollStatus, pollInterval);
        }, { timeout: isMobile ? 3000 : 2000 }); // 移动端更长的超时时间
      } else {
        // 降级到立即执行
        pollStatus();
        intervalId = setInterval(pollStatus, pollInterval);
      }
    };

    // 🔥 关键优化：延迟启动轮询，让LCP元素先渲染完成
    const delayPolling = setTimeout(startPolling, 200);
    
    return () => {
      clearTimeout(delayPolling);
      clearInterval(intervalId);
    };

    return () => {
      clearInterval(intervalId);
    };
  }, [taskId, currentStatus, isWebhook, onStatusUpdate]);

  // 根据当前状态获取显示内容
  const currentStatusDisplay = statusMap[currentStatus] || statusMap.pending;
  const StatusIcon = currentStatusDisplay.icon;

  // 进度条和队列展示的状态提示
  const getStatusMessage = () => {
    const remainingTime = formatTime(Math.max(0, actualEstimatedTime - elapsedTime));
    
    if (hasAccess) {
      return t('GenerationProgress.queue.priorityProcessing', { remainingTime });
    } else {
      if (currentProgress < 30) {
        return t('GenerationProgress.queue.inQueue', { remainingTime });
      } else if (currentProgress < 60) {
        return t('GenerationProgress.queue.generationStarted', { remainingTime });
      } else {
        return t('GenerationProgress.queue.finalProcessing', { remainingTime });
      }
    }
  };

  return (
    <div className="w-full space-y-3">
      {/* 🔥 LCP优化：延迟升级提示渲染，避免阻塞LCP元素 */}
      {!hasAccess && (
        <div className="rounded-lg border border-green-500/30 bg-gradient-to-r from-green-900/20 to-cyan-900/20 p-3 backdrop-blur-sm">
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
            <p className="flex-1 text-sm text-green-300">
              {t('GenerationProgress.upgrade.hint')}
            </p>
            {onUpgrade && (
              <Button
                className="h-auto rounded-md bg-gradient-to-r from-green-500 to-cyan-500 px-3 py-1.5 text-sm font-medium text-white transition-all duration-300 hover:opacity-90"
                onClick={onUpgrade}
                // 🔥 关键优化：添加loading="lazy"属性
                loading="lazy"
              >
                {t('GenerationProgress.upgrade.button')}
              </Button>
            )}
          </div>
        </div>
      )}

      <Card className="w-full overflow-hidden rounded-lg border border-gray-700/50 bg-gray-900/90 shadow-lg backdrop-blur-sm">
        {/* 🔥 LCP优化：简化装饰线，减少渲染复杂度 */}
        <div className="h-0.5 bg-green-500"></div>
        
        <CardHeader className="pb-3 pt-4">
          <div className="mb-2 flex items-center justify-between">
            <CardTitle className="flex items-center text-lg font-semibold">
              <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-green-500/20 border border-green-500/30">
                <StatusIcon className={`h-4 w-4 ${currentStatusDisplay.iconClass}`} />
              </div>
              <span className="text-white">
                {currentStatusDisplay.title}
              </span>
            </CardTitle>

            {/* 简化的状态指示灯 */}
            <div className={`h-2 w-2 rounded-full ${
              currentStatus === 'processing' ? 'bg-green-500' :
              currentStatus === 'pending' ? 'bg-green-500' :
              currentStatus === 'completed' ? 'bg-green-500' :
              'bg-red-500'
            }`}></div>
          </div>
          
          <CardDescription className="text-sm text-gray-300">
            {currentStatusText || currentStatusDisplay.description}
          </CardDescription>
        </CardHeader>

        <CardContent className="pb-6 pt-2">
          {/* 简化的进度条 */}
          <div className="mb-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">{t('GenerationProgress.progress.label')}</span>
              <span className="font-mono font-semibold text-green-400">
                {currentProgress.toFixed(0)}%
              </span>
            </div>
            
            <div className="h-2 overflow-hidden rounded-full bg-gray-800">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  currentStatus === 'completed'
                    ? 'bg-green-500'
                    : currentStatus === 'failed'
                      ? 'bg-red-500'
                      : 'bg-gradient-to-r from-green-500 to-cyan-500'
                }`}
                style={{ width: `${currentProgress}%` }}
              />
            </div>
          </div>

          {/* 紧凑的时间信息 */}
          {(currentStatus === 'processing' || currentStatus === 'pending') && (
            <div className="mb-4 rounded-lg border border-green-500/20 bg-green-900/10 p-3">
              <div className="grid grid-cols-3 gap-3 text-center text-sm">
                <div>
                  <div className="text-xs text-gray-400">{t('GenerationProgress.progress.estimated')}</div>
                  <div className="font-mono font-semibold text-green-300">
                    {formatTime(actualEstimatedTime)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">{t('GenerationProgress.progress.elapsed')}</div>
                  <div className="font-mono font-semibold text-green-300">
                    {formatTime(elapsedTime)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">{t('GenerationProgress.progress.remaining')}</div>
                  <div className="font-mono font-semibold text-cyan-300">
                    {formatTime(Math.max(0, actualEstimatedTime - elapsedTime))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 🔥 LCP优化：简化队列信息显示 */}
          {!hasAccess && currentStatus !== 'completed' && currentStatus !== 'failed' && (
            <div className="rounded-lg border border-orange-500/20 bg-orange-900/10 p-4">
              <div className="mb-3 flex items-center justify-center gap-2">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className={`flex h-7 w-7 items-center justify-center rounded-full text-sm font-medium ${
                      i < queuePosition 
                        ? 'bg-gray-700 text-gray-400' 
                        : i === queuePosition 
                          ? 'bg-green-500 text-white' 
                          : 'bg-green-500/20 text-green-400 border border-green-500/30'
                    }`}
                  >
                    {i + 1}
                  </div>
                ))}
              </div>
              <div className="space-y-1 text-center">
                <p className="text-sm text-gray-300">{getStatusMessage()}</p>
                <p className="text-xs text-gray-400">
                  {t('GenerationProgress.queue.position')}: <span className="font-semibold text-green-400">#{queuePosition}</span> / 5
                </p>
              </div>
            </div>
          )}

          {/* 简化的付费用户信息 */}
          {hasAccess && currentStatus !== 'completed' && currentStatus !== 'failed' && (
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-900/10 p-3 text-center">
              <div className="mb-2 inline-flex items-center rounded-full bg-emerald-500/20 px-3 py-1">
                <div className="mr-2 h-1.5 w-1.5 rounded-full bg-emerald-400"></div>
                <span className="text-sm font-medium text-emerald-300">{t('GenerationProgress.priority.title')}</span>
              </div>
              <p className="text-sm text-gray-300">{getStatusMessage()}</p>
            </div>
          )}
        </CardContent>

        {error && (
          <CardFooter className="border-t border-red-500/20 bg-red-900/10 px-4 py-3">
            <div className="flex items-center text-red-400">
              <AlertTriangle className="mr-2 h-4 w-4" />
              <span className="text-sm">{t('GenerationProgress.error')}: {error}</span>
            </div>
          </CardFooter>
        )}
      </Card>
    </div>
  );
};

// 🔥 LCP优化：使用React.memo包装，避免不必要的重渲染
export default React.memo(GenerationProgress);
